import { Injectable } from "@angular/core";
import { inject } from "@angular/core";
import { C_Features, GlobalVarsModel } from "@models/index";
import { PaymentMethodConfig } from "@models/payments/PaymentMethodConfig";
import { TrackingService } from "@providers/TrackingService";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { TransactionActionResult } from "../../models/C_Transactions";
import { ErrorService } from "../ErrorService";
import { UserService } from "../UserService";
import { NewBaseService } from "../newBase.service";
import { AstropayEPG } from "./AstropayEPG";
import { BankAccountMethodBehaviour } from "./BankTransfer";
import { BankcvuMethodBehaviour } from "./BankcvuMethodBehaviour";
import { BizumMethodBehaviour } from "./Bizum";
import { RedsysMethodBehaviour } from "./BizumRedsys";
import { CoinspaidDepositBehaviour } from "./CoinspaidDeposit";
import { CrowCasinoDepositMethodBehaviour } from "./CrowCasinoDeposit";
import { CyberSourceMethodBehaviour } from "./CyberSource";
import { EPGMethodBehaviour } from "./EPG";
import { EPGMuchBetter } from "./EPGMuchBetter";
import { LocalCodereMethodBehaviour } from "./LocalCodere";
import { LotbaPaymentMethodBehaviour } from "./Lotba";
import { MercadoPagoMethodBehaviour } from "./MercadoPago";
import { MercadoPagoRedirectMethodBehaviour } from "./MercadoPagoRedirect";
import { NetellerMethodBehaviour } from "./Netteler";
import {
  AlmacenesExitoOxxoBankPaymentMethodBehaviour,
  AmericanExpressOxxoBankPaymentMethodBehaviour,
  AztecaBankOxxoBankPaymentMethodBehaviour,
  BalotoOxxoBankPaymentMethodBehaviour,
  BanamexOxxoBankPaymentMethodBehaviour,
  BancolombiaBankOxxoBankPaymentMethodBehaviour,
  BancomerOxxoBankPaymentMethodBehaviour,
  BanorteBankPaymentMethodBehaviour,
  CarullaOxxoBankPaymentMethodBehaviour,
  CucutaOxxoBankPaymentMethodBehaviour,
  GanaBankOxxoBankPaymentMethodBehaviour,
  GanaCercaBankOxxoBankPaymentMethodBehaviour,
  MasterCardOxxoBankPaymentMethodBehaviour,
  PagoTodoOxxoBankPaymentMethodBehaviour,
  PaynetOxxoBankPaymentMethodBehaviour,
  SantanderOxxoBankPaymentMethodBehaviour,
  SpeiBankPaymentMethodBehaviour,
  ToditoOxxoBankPaymentMethodBehaviour,
  VisaOxxoBankPaymentMethodBehaviour,
} from "./OxxoBank";
import { OxxoCashPaymentMethodBehaviour } from "./OxxoCash";
import { PSEOxxoBankPaymentMethodBehaviour } from "./PSEOxxoBankPaymentMethodBehaviour";
import { Pagos360MethodBehaviour } from "./Pagos360";
import { Pagos360TransBankMethodBehaviour } from "./Pagos360TransBank";
import { PagueloFacilPaymentMethodBehaviour } from "./PagueloFacil";
import { PayUPSEPaymentMethodBehaviour, PayUPaymentMethodBehaviour } from "./PayU";
import {
  BalotoPayValidaCashPaymentMethodBehaviour,
  DimonexPayValidaCashPaymentMethodBehaviour,
} from "./PayValida";
import { PuntoRedPayValidaCashPaymentMethodBehaviour } from "./PayValida";
import { PaymentMethodBehaviour } from "./PaymentMethodBase";
import { PaynetPaymentMethodBehaviour } from "./Paynet";
import { PaypalPaymentMethodBehaviour, PaypalPaymentMethodBehaviourResult } from "./Paypal";
import { PaysafecardMethodBehaviour } from "./PaysafeCard";
import { Praxis_BizumMethodBehaviour } from "./Praxis_Bizum";
import { Praxis_MercadoPagoMethodBehaviour } from "./Praxis_MercadoPago";
import { Praxis_SKRILLMethodBehaviour } from "./Praxis_Skrill";
import { Praxis_VISAMethodBehaviour } from "./Praxis_VISA";
import { PuntoPagoMethodBehaviour } from "./PuntoPago";
import { RapidTransferMethodBehaviour } from "./RapidTransfer";
import { RedsysCardsMethodBehaviour } from "./RedsysCards";
import { SKRILLMethodBehaviour } from "./Skrill";
import { TelePayPaymentMethodBehaviourCO } from "./TelePay";
import { TeleingresoESPaymentMethodBehaviour } from "./Teleingreso";
import { TpagaDepositBehaviour } from "./TpagaDeposit";
import { TrueLayerMethodBehaviour } from "./TrueLayer";
import { VisaBBVAMexPaymentMethodBehaviour } from "./VisaBBVAMex";

@Injectable({ providedIn: "root" })
export class PaymentMethodFactory {
  globalVars!: GlobalVarsModel;
  features: C_Features;
  paypalShowErrorCallback: (paymentMethodBehaviourResult: PaypalPaymentMethodBehaviourResult) => any;
  paypalExecuteOkCallback: (transaction: TransactionActionResult) => any;

  newBaseService = inject(NewBaseService);
  errorService = inject(ErrorService);
  userService = inject(UserService);
  trackingService = inject(TrackingService);

  public constructor() {
    this.newBaseService.getVars.subscribe((data) => {
      this.globalVars = data;
      this.features = this.globalVars.FEATURES;
      this.features.Payments_PRAXIS_ES_Redsys_Deposit = this.features.Payments_PRAXIS_ES_Redsys_Deposit;
    });
  }

  initialize(
    paypalShowErrorCallback: (paymentMethodBehaviourResult: PaypalPaymentMethodBehaviourResult) => any,
    paypalExecuteOkCallback: (transaction: TransactionActionResult) => any,
  ) {
    this.paypalShowErrorCallback = paypalShowErrorCallback;
    this.paypalExecuteOkCallback = paypalExecuteOkCallback;
  }

  payments(astropayBankMethods: Array<any>): Observable<Array<PaymentMethodBehaviour>> {
    let result: Observable<Array<PaymentMethodBehaviour>> = of([]);
    result = this.userService.getPaymentMethods().pipe(map((x) => x.map((y) => this.map(y, astropayBankMethods))));
    return result;
  }

  private map(input: any, astropayBankMethods: Array<any>): PaymentMethodBehaviour {
    let result: PaymentMethodBehaviour = null;

    switch (input.PaymentMethodName) {
      case PaymentMethodConfig.EPG.methodName:
        if (this.features.Payments_PRAXIS_ES_Redsys_Deposit === true) {
          result = new Praxis_VISAMethodBehaviour(this.userService, this.errorService, this.trackingService);
        } else {
          result = new EPGMethodBehaviour(this.userService, this.errorService, this.trackingService);
        }
        break;
      case PaymentMethodConfig.OXXO_PAYNET.methodName:
        result = new PaynetOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.PAYSAFE.methodName:
        result = new PaysafecardMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.BIZUM.methodName:
        if (this.features.Payments_PRAXIS_ES_Bizum_Deposit === true) {
          result = new Praxis_BizumMethodBehaviour(this.userService, this.errorService, this.trackingService);
        } else {
          result = new BizumMethodBehaviour(this.userService, this.errorService, this.trackingService);
        }
        break;
      case PaymentMethodConfig.REDSYS_CARDS.methodName:
        result = new RedsysCardsMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.REDSYS_BIZUM.methodName:
        result = new RedsysMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.PAYPAL.methodName:
        result = new PaypalPaymentMethodBehaviour(
          this.userService,
          this.paypalShowErrorCallback,
          this.paypalExecuteOkCallback,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.BANK_TRANSFER.methodName:
        result = new BankAccountMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.TELEINGRESOES.methodName:
        result = new TeleingresoESPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.TELEPAYCO.methodName:
        result = new TelePayPaymentMethodBehaviourCO(
          this.userService,
          false,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.BALOTO.methodName: // merge in master is comented, please check
        result = new BalotoPayValidaCashPaymentMethodBehaviour(
          this.userService,
          false,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.EFECTY.methodName:
        result = new DimonexPayValidaCashPaymentMethodBehaviour(
          this.userService,
          false,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.PUNTORED.methodName:
        result = new PuntoRedPayValidaCashPaymentMethodBehaviour(
          this.userService,
          false,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_CASH.methodName:
        result = new OxxoCashPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.OXXO_AMEX.methodName:
        result = new AmericanExpressOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_BALOTO.methodName:
        result = new BalotoOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_PSE.methodName:
        result = new PSEOxxoBankPaymentMethodBehaviour(
          this.userService,
          "PC",
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_DAVIPLATA_PSE.methodName:
        result = new PSEOxxoBankPaymentMethodBehaviour(
          this.userService,
          "PC",
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_NEQUI_PSE.methodName:
        result = new PSEOxxoBankPaymentMethodBehaviour(
          this.userService,
          "PC",
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_SPEI.methodName:
        result = new SpeiBankPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.VISA_BANCOMER.methodName:
        result = new VisaBBVAMexPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.OXXO_BANCOMER.methodName:
        result = new BancomerOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_BANAMEX.methodName:
        result = new BanamexOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_SANTANDER.methodName:
        result = new SantanderOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_BANORTE.methodName:
        result = new BanorteBankPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.OXXO_TODITO.methodName:
        result = new ToditoOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_GANA_CERCA.methodName:
        result = new GanaCercaBankOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_AZTECABANK.methodName:
        result = new AztecaBankOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_CARULLA.methodName:
        result = new CarullaOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_GANABANK.methodName:
        result = new GanaBankOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_ALMACENES_EXITO.methodName:
        result = new AlmacenesExitoOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_PAGOTODO.methodName:
        result = new PagoTodoOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.OXXO_CUCUTA.methodName:
        result = new CucutaOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.PAYU.methodName:
        result = new PayUPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.PAYUPSE.methodName:
        result = new PayUPSEPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.PAYNET.methodName:
        result = new PaynetPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.SKRILL.methodName:
        if (this.features.Payments_PRAXIS_ES_Skrill_Deposit === true) {
          return new Praxis_SKRILLMethodBehaviour(
            this.userService,
            this.errorService,
            this.newBaseService,
            this.trackingService,
          );
        } else {
          result = new SKRILLMethodBehaviour(
            this.userService,
            this.errorService,
            this.newBaseService,
            this.trackingService,
          );
        }
        break;
      case PaymentMethodConfig.CYBERSOURCE.methodName:
        result = new CyberSourceMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
          this.newBaseService,
        );
        break;
      case PaymentMethodConfig.RAPIDTRANSFER.methodName:
        result = new RapidTransferMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.NETELLER.methodName:
        result = new NetellerMethodBehaviour(
          this.userService,
          this.errorService,
          this.newBaseService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.LOCAL.methodName:
        result = new LocalCodereMethodBehaviour(this.userService, this.errorService);
        break;
      case PaymentMethodConfig.PUNTO_PAGO.methodName:
        result = new PuntoPagoMethodBehaviour(this.userService, this.errorService);
        break;
      case PaymentMethodConfig.OXXO_BANCOLOMBIA.methodName:
        result = new BancolombiaBankOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.ASTROPAY_EPG.methodName:
        result = new AstropayEPG(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.OXXO_VISA.methodName:
        result = new VisaOxxoBankPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.OXXO_MASTERCARD.methodName:
        result = new MasterCardOxxoBankPaymentMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
        );
        break;
      case PaymentMethodConfig.MERCADO_PAGO.methodName:
        result = new MercadoPagoMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.MERCADO_PAGO_REDIRECT.methodName:
        if (this.features.Payments_PRAXIS_CABA_MercadoPago_Deposit === true) {
          result = new Praxis_MercadoPagoMethodBehaviour(
            this.userService,
            this.errorService,
            this.trackingService,
          );
        } else {
          result = new MercadoPagoRedirectMethodBehaviour(
            this.userService,
            this.errorService,
            this.trackingService,
          );
        }
        break;
      case PaymentMethodConfig.MERCADO_PAGO_REDIRECT_CARDS.methodName:
        result = new MercadoPagoRedirectMethodBehaviour(
          this.userService,
          this.errorService,
          this.trackingService,
          true,
        );
        break;
      case PaymentMethodConfig.PAGOS_360.methodName:
        result = new Pagos360MethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.PAGOS_TRANS_360.methodName:
        result = new Pagos360TransBankMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.T_PAGA_DEPOSIT.methodName:
        result = new TpagaDepositBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.COINS_PAID.methodName:
        result = new CoinspaidDepositBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.EPG_MUCH_BETTER.methodName:
        result = new EPGMuchBetter(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.CVU_BANK.methodName:
        result = new BankcvuMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.PAGUELO_FACIL.methodName:
        result = new PagueloFacilPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.TRUE_LAYER.methodName:
        result = new TrueLayerMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.CROWN_CASINO_DEPOSIT.methodName:
        result = new CrowCasinoDepositMethodBehaviour(this.userService, this.errorService, this.trackingService);
        break;
      case PaymentMethodConfig.LOTBA.methodName:
        result = new LotbaPaymentMethodBehaviour(this.userService, this.errorService, this.trackingService);
    }
    if (result) {
      result.name = input.Name;
      result.image = input.Image;
      result.track = input.Track;
      result.minDepositAmount = input.minDepositAmount;
      result.maxDepositAmount = input.maxDepositAmount;
      result.amountButtons = input.amountButtons;
      result.amount = input.amount;
      result.isRecommended = input.isRecommended || false;
    }
    return result;
  }
}
